import { useState, type ReactNode } from "react";
import { useCustom, useTranslate } from "@refinedev/core";
import { Button, CalendarIcon, CheckIcon, ChevronRightIcon, ClockIcon, DropletIcon, FileTextIcon, Modal, PencilIcon, PlusIcon, StatusBadge, type StatusTone } from "@fawkes/ui";

import { uitgevoerdDoor } from "../uitgevoerdDoor";
import { NieuwVoedingsplanDialoog, type OudPlan, type Samenstelling, type SamenstellingRegel, type VoedingSoort } from "./Voedingsplan";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// ── Voedingsschema's (Maarten, 2026-10-11) ────────────────────────────────────────────────────────
//
// Eén knop "Voedingsschema" in de voedingstegel opent dit venster. Drie stappen, zoals het ontwerp:
//   1. het overzicht van alle schema's van het dier, het huidige bovenaan, met "+ Nieuw schema";
//   2. de details van één schema: periode, wie het maakte, wat erin zit, en elke voerbeurt eronder;
//   3. een nieuw schema, leeg of op basis van het schema dat je bekeek.
// Een nieuw schema vervangt het vorige; het vorige blijft in het overzicht staan.

export type SchemaSoort = "BROK" | "NATVOER" | "VOCHT" | "GEMENGD";

export interface SchemaKaart {
  id: number; huidig: boolean; titel: string; soort: SchemaSoort; maaltijdenPerDag: number | null; portie: string;
  voertijden: string[]; aangemaaktOp: string | null; door: string | null; activeFrom: string | null; activeTo: string | null;
  kcalPerDag: number | null;
}

export interface SchemaDetail extends SchemaKaart {
  samenvatting: string; opmerking: string | null; regels: SamenstellingRegel[];
  feedType: string | null; brand: string | null; amountText: string | null; feedingTimes: string | null;
  voerbeurten: { fedAt: string | null; gegeven: number | null; gegeten: number | null; gedrag: string | null; opmerking: string | null; initialen: string | null; door?: string | null }[];
  gegevenGram: number; gegetenGram: number;
}

type Stap = { soort: "lijst" } | { soort: "detail"; id: number } | { soort: "nieuw"; basis: SchemaDetail | null }
  | { soort: "bewerk"; basis: SchemaDetail };

const datum = (iso: string | null) => {
  const m = iso?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "-";
};
const datumTijd = (iso: string | null) => {
  const m = iso?.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]} ${m[4]}` : "-";
};
const getal = (n: number) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10).replace(".", ","));

/** Het icoon bij een schema: een voerbak voor brok, een blik voor natvoer, een druppel voor vocht of dieet. */
export function SchemaIcoon({ soort }: { soort: SchemaSoort }) {
  if (soort === "NATVOER") {
    return (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v12c0 1.7 3.1 3 7 3s7-1.300 7-3V6" /><path d="M5 12c0 1.700 3.100 3 7 3s7-1.300 7-3" />
      </svg>
    );
  }
  if (soort === "VOCHT" || soort === "GEMENGD") return <DropletIcon width={26} height={26} aria-hidden="true" />;
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 13h18l-1.500 5.500a2 2 0 0 1-2 1.500h-11a2 2 0 0 1-2-1.500L3 13Z" /><path d="M6 13c0-3 2.500-5 6-5s6 2 6 5" /><path d="M9.500 6.500 10 5M14.500 6.500 14 5" />
    </svg>
  );
}

const GEDRAG: Record<string, { label: string; toon: StatusTone }> = {
  ALLES_GEGETEN: { label: "animalDetailView.feeding.behavior.ALLES_GEGETEN", toon: "success" },
  GOED_GEGETEN: { label: "animalDetailView.feeding.behavior.GOED_GEGETEN", toon: "success" },
  BIJNA_OP: { label: "animalDetailView.feeding.behavior.BIJNA_OP", toon: "warning" },
  SLECHT_GEGETEN: { label: "animalDetailView.feeding.behavior.SLECHT_GEGETEN", toon: "danger" },
};

/** Ontbijt, middagvoer of avondvoer, naar het uur van de voerbeurt. */
function moment(iso: string | null, t: (k: string) => string): string {
  const uur = Number(iso?.match(/T(\d{2}):/)?.[1] ?? NaN);
  if (!Number.isFinite(uur)) return "-";
  return t(uur < 11 ? "animalDetailView.feeding.meal.breakfast" : uur < 16 ? "animalDetailView.feeding.meal.lunch" : "animalDetailView.feeding.meal.dinner");
}

export function VoedingsschemaVenster({ animalId, open, onClose, onGewijzigd, meteenNieuw = false }: {
  animalId: string;
  open: boolean;
  onClose: () => void;
  /** Er is een schema bijgekomen: de tegel haalt zijn gegevens opnieuw op. */
  onGewijzigd: () => void;
  /** Open meteen op "Nieuw schema" (een taak "Voeding vastleggen"). */
  meteenNieuw?: boolean;
}) {
  const t = useTranslate();
  const [stap, setStap] = useState<Stap>({ soort: "lijst" });
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setStap(meteenNieuw ? { soort: "nieuw", basis: null } : { soort: "lijst" });
  }
  const lijst = useCustom<SchemaKaart[]>({ url: `animals/${animalId}/feeding/plannen`, method: "get", queryOptions: { enabled: open } });
  const schemas = Array.isArray(lijst.data?.data) ? lijst.data!.data : [];
  const detailId = stap.soort === "detail" ? stap.id : null;
  const detailQuery = useCustom<SchemaDetail>({
    url: `animals/${animalId}/feeding/plannen/${detailId}`, method: "get", queryOptions: { enabled: open && detailId != null },
  });
  const detail = detailId != null && detailQuery.data?.data && Array.isArray(detailQuery.data.data.voerbeurten) ? detailQuery.data.data : null;

  if (!open) return null;

  if (stap.soort === "nieuw" || stap.soort === "bewerk") {
    const basis = stap.basis;
    const bewerken = stap.soort === "bewerk";
    // Het schema waar je vanuit gaat: het bekeken schema, anders het huidige.
    const huidigeKaart = schemas.find((s) => s.huidig) ?? null;
    const samenstelling: Samenstelling | null = basis && basis.regels.length > 0
      ? { maaltijdenPerDag: basis.maaltijdenPerDag, regels: basis.regels, portie: basis.regels.reduce((s, r) => s + r.hoeveelheid, 0), kcalPerDag: basis.kcalPerDag, kcalVolledig: basis.regels.every((r) => r.kcalPer100 != null) }
      : null;
    const oud: OudPlan | null = basis && basis.regels.length === 0
      ? { feedType: basis.feedType, brand: basis.brand, amountText: basis.amountText, feedingTimes: basis.feedingTimes }
      : null;
    const referentie = basis ? `${basis.titel} · ${basis.portie}` : huidigeKaart ? `${huidigeKaart.titel} · ${huidigeKaart.portie}` : null;
    return (
      <NieuwVoedingsplanDialoog
        animalId={animalId}
        open
        huidig={samenstelling}
        huidigeTekst={referentie}
        oudPlan={oud}
        bewerkId={bewerken && basis ? basis.id : null}
        startTijden={basis ? basis.voertijden : undefined}
        startOpmerking={basis ? basis.opmerking : undefined}
        onClose={() => setStap(basis ? { soort: "detail", id: basis.id } : { soort: "lijst" })}
        onOpgeslagen={() => {
          void lijst.refetch(); onGewijzigd();
          if (bewerken && basis) { void detailQuery.refetch(); setStap({ soort: "detail", id: basis.id }); } else setStap({ soort: "lijst" });
        }}
      />
    );
  }

  if (stap.soort === "detail") {
    return (
      <Modal open size="wide" title={t("voedingsschema.detail.titel")} subtitle={t("voedingsschema.detail.uitleg")} onClose={onClose}
        footer={
          <>
            <Button variant="secondary" onClick={() => setStap({ soort: "lijst" })}>{t("voedingsschema.terug")}</Button>
            {detail?.huidig && (
              // Alleen het schema dat nu geldt is te bewerken; een eerder schema blijft zoals het gegeven is.
              <Button variant="secondary" onClick={() => setStap({ soort: "bewerk", basis: detail })}>
                <PencilIcon width={15} height={15} /> {t("voedingsschema.bewerken")}
              </Button>
            )}
            <Button variant="primary" disabled={!detail} onClick={() => detail && setStap({ soort: "nieuw", basis: detail })}>
              <PlusIcon width={16} height={16} /> {t("voedingsschema.opBasisVan")}
            </Button>
          </>
        }>
        {detailQuery.isLoading || !detail ? (
          <p className="vsch-muted">{detailQuery.isError ? t("voedingsplan.fout") : t("common.loading")}</p>
        ) : (
          <SchemaDetails d={detail} />
        )}
      </Modal>
    );
  }

  return (
    <Modal open title={t("voedingsschema.titel")} subtitle={t("voedingsschema.uitleg")} onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>{t("common.close")}</Button>}>
      <div className="vsch">
        <div className="vsch-kop">
          <Button variant="primary" onClick={() => setStap({ soort: "nieuw", basis: null })}>
            <PlusIcon width={16} height={16} /> {t("voedingsschema.nieuw")}
          </Button>
        </div>
        {lijst.isLoading ? (
          <p className="vsch-muted">{t("common.loading")}</p>
        ) : schemas.length === 0 ? (
          <p className="vsch-leeg">{t("voedingsschema.geen")}</p>
        ) : (
          <ul className="vsch-lijst">
            {schemas.map((s) => (
              <li key={s.id}>
                <button type="button" className={`vsch-kaart${s.huidig ? " is-huidig" : ""}`} onClick={() => setStap({ soort: "detail", id: s.id })}>
                  <span className="vsch-kaart__icoon"><SchemaIcoon soort={s.soort} /></span>
                  <span className="vsch-kaart__tekst">
                    <span className="vsch-kaart__titelrij">
                      <strong>{s.titel}</strong>
                      {s.huidig && <span className="vsch-huidig"><CheckIcon width={13} height={13} /> {t("voedingsschema.huidig")}</span>}
                    </span>
                    <span className="vsch-kaart__regel">
                      <span>{s.portie}</span>
                      {s.voertijden.map((tijd) => <span key={tijd} className="vsch-tijd">{tijd}</span>)}
                      {s.kcalPerDag != null && <span className="vsch-muted">{getal(Math.round(s.kcalPerDag))} kcal/dag</span>}
                    </span>
                    <span className="vsch-kaart__meta">
                      {t("voedingsschema.aangemaaktOp")} {s.aangemaaktOp ? datumTijd(s.aangemaaktOp) : datum(s.activeFrom)}
                      {s.door && <> <span aria-hidden="true">|</span> {t("voedingsschema.door")}: {s.door}</>}
                    </span>
                  </span>
                  <ChevronRightIcon width={18} height={18} aria-hidden="true" className="vsch-kaart__pijl" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

function Blok({ icoon, titel, children, className }: { icoon?: ReactNode; titel: string; children: ReactNode; className?: string }) {
  return (
    <section className={`vsch-blok${className ? ` ${className}` : ""}`}>
      {icoon && <span className="vsch-blok__icoon" aria-hidden="true">{icoon}</span>}
      <div className="vsch-blok__inhoud">
        <h4>{titel}</h4>
        {children}
      </div>
    </section>
  );
}

/** De details van één schema: zoals het middelste scherm van het ontwerp. */
export function SchemaDetails({ d }: { d: SchemaDetail }) {
  const t = useTranslate();
  const gram = d.regels.filter((r) => r.soort !== "VOCHT").reduce((s, r) => s + r.hoeveelheid, 0);
  const ml = d.regels.filter((r) => r.soort === "VOCHT").reduce((s, r) => s + r.hoeveelheid, 0);
  const per = (n: number) => [gram > 0 ? `${getal(gram * n)} g` : null, ml > 0 ? `${getal(ml * n)} ml` : null].filter(Boolean).join(" + ");
  const soorten = new Set<VoedingSoort>(d.regels.map((r) => r.soort));
  const metKcal = d.regels.filter((r) => r.kcalPer100 != null);
  const pct = d.gegevenGram > 0 ? Math.round((d.gegetenGram / d.gegevenGram) * 100) : null;
  return (
    <div className="vsch-detail">
      <div className={`vsch-banner${d.huidig ? " is-huidig" : ""}`}>
        <span className="vsch-banner__teken" aria-hidden="true">{d.huidig ? <CheckIcon width={18} height={18} /> : <ClockIcon width={18} height={18} />}</span>
        <span>
          <strong>{t(d.huidig ? "voedingsschema.huidigSchema" : "voedingsschema.eerderSchema")}</strong>
          <span>{t(d.huidig ? "voedingsschema.huidigUitleg" : "voedingsschema.eerderUitleg")}</span>
        </span>
      </div>

      <div className="vsch-rij vsch-rij--2">
        <Blok icoon={<CalendarIcon width={20} height={20} />} titel={t("voedingsschema.periode")}>
          <p>{t("voedingsschema.actiefSinds")} {datum(d.activeFrom)}</p>
          <p>{d.activeTo ? `${t("voedingsschema.tot")} ${datum(d.activeTo)}` : t("voedingsschema.nogActief")}</p>
        </Blok>
        <Blok icoon={<FileTextIcon width={20} height={20} />} titel={t("voedingsschema.aangemaaktOp")}>
          <p>{d.aangemaaktOp ? datumTijd(d.aangemaaktOp) : datum(d.activeFrom)}</p>
          <p>{d.door ? `${t("voedingsschema.door")}: ${d.door}` : "-"}</p>
        </Blok>
      </div>

      <div className="vsch-rij vsch-rij--info">
        <section className="vsch-blok vsch-blok--tabel">
          <div className="vsch-blok__inhoud">
            <h4>{t("voedingsschema.informatie")}</h4>
            <dl>
              {d.regels.length > 0 ? d.regels.map((r, i) => (
                <div key={i}><dt>{t(`voedingsplan.soort.${r.soort}`)}</dt><dd>{getal(r.hoeveelheid)} {r.eenheid} {r.productNaam}</dd></div>
              )) : (
                <>
                  <div><dt>{t("voedingsschema.voertype")}</dt><dd>{d.feedType ?? "-"}</dd></div>
                  <div><dt>{t("voedingsschema.product")}</dt><dd>{d.brand ?? "-"}</dd></div>
                </>
              )}
              <div><dt>{t("voedingsschema.frequentie")}</dt><dd>{d.maaltijdenPerDag ? t("voedingsplan.keerPerDag").replace("%s", String(d.maaltijdenPerDag)) : d.voertijden.length ? t("voedingsplan.keerPerDag").replace("%s", String(d.voertijden.length)) : "-"}</dd></div>
              <div><dt>{t("voedingsplan.portie")}</dt><dd>{d.regels.length > 0 ? per(1) : d.amountText ?? "-"}</dd></div>
              {d.regels.length > 0 && d.maaltijdenPerDag && (
                <div><dt>{t("voedingsschema.totaalPerDag")}</dt><dd>{per(d.maaltijdenPerDag)}</dd></div>
              )}
            </dl>
          </div>
        </section>
        <div className="vsch-kolom">
          <Blok icoon={<ClockIcon width={20} height={20} />} titel={t("voedingsschema.voertijden")}>
            {d.voertijden.length > 0
              ? <div className="vsch-chips">{d.voertijden.map((x) => <span key={x} className="vsch-tijd">{x}</span>)}</div>
              : <p>-</p>}
          </Blok>
          {d.regels.length > 0 && (
            <Blok titel={t("voedingsschema.samenstelling")}>
              <div className="vsch-chips">
                {(["BROK", "NATVOER", "VOCHT"] as VoedingSoort[]).map((s) => (
                  <span key={s} className={`vsch-chip${soorten.has(s) ? " is-aan" : ""}`}>{t(`voedingsplan.soort.${s}`)}</span>
                ))}
              </div>
            </Blok>
          )}
        </div>
      </div>

      <div className="vsch-rij vsch-rij--2">
        <Blok icoon={<DropletIcon width={20} height={20} />} titel={t("voedingsschema.calorisch")}>
          {d.kcalPerDag != null ? <p><strong>{getal(Math.round(d.kcalPerDag))} kcal</strong> {t("voedingsschema.perDag")}</p> : <p>{t("voedingsschema.kcalOnbekend")}</p>}
          {metKcal.map((r, i) => <p key={i} className="vsch-muted">{r.productNaam}: {getal(r.kcalPer100 as number)} kcal / 100 {r.eenheid}</p>)}
        </Blok>
        <Blok icoon={<FileTextIcon width={20} height={20} />} titel={t("animalDetailView.feeding.field.note")}>
          <p>{d.opmerking || "-"}</p>
        </Blok>
      </div>

      <section className="vsch-gegeven">
        <header>
          <h4>{t("voedingsschema.gegeven")}</h4>
          <span className="vsch-teller">
            {d.voerbeurten.length === 1 ? t("voedingsschema.eenRegistratie") : t("voedingsschema.registraties").replace("%s", String(d.voerbeurten.length))}
            {pct != null && ` · ${pct}% ${t("voedingsschema.opgegeten")}`}
          </span>
        </header>
        {d.voerbeurten.length === 0 ? (
          <p className="vsch-muted vsch-gegeven__leeg">{t("voedingsplan.geenVoerbeurten")}</p>
        ) : (
          <div className="vsch-tabelwrap">
            <IndeelbareTabel
              tabelKey="voedingsschema-voerbeurten"
              columns={[
                { key: "datum", header: t("voedingsschema.kolom.datum"), cell: (v) => datum(v.fedAt) },
                { key: "tijd", header: t("voedingsschema.kolom.tijd"),
                  cell: (v) => v.fedAt?.match(/T(\d{2}:\d{2})/)?.[1] ?? "-" },
                { key: "moment", header: t("voedingsschema.kolom.moment"), cell: (v) => moment(v.fedAt, t) },
                { key: "gegeven", header: t("voedingsplan.gegeven"),
                  cell: (v) => <span className="is-getal">{v.gegeven != null ? `${v.gegeven} g` : "-"}</span> },
                { key: "gegeten", header: t("voedingsplan.gegeten"),
                  cell: (v) => <span className="is-getal">{v.gegeten != null ? `${v.gegeten} g` : "-"}</span> },
                {
                  key: "status", header: t("voedingsschema.kolom.status"),
                  cell: (v) => {
                    const g = v.gedrag ? GEDRAG[v.gedrag] : undefined;
                    // Nul gram gegeten terwijl er wél iets stond: geweigerd, en dat is iets anders dan
                    // "geen gedrag genoteerd".
                    const geweigerd = v.gegeten === 0 && (v.gegeven ?? 0) > 0;
                    return geweigerd
                      ? <StatusBadge tone="danger">{t("voedingsschema.geweigerd")}</StatusBadge>
                      : g ? <StatusBadge tone={g.toon}>{t(g.label)}</StatusBadge> : "-";
                  },
                },
                {
                  key: "notitie", header: t("animalDetailView.feeding.col.note"),
                  cell: (v) => (
                    <>
                      {v.opmerking || "-"}
                      {uitgevoerdDoor(v.door, v.initialen)
                        ? <span className="vsch-muted"> · {uitgevoerdDoor(v.door, v.initialen)}</span>
                        : null}
                    </>
                  ),
                },
              ]}
              rows={d.voerbeurten}
              getRowKey={(v) => String(v.fedAt ?? "") + String(v.gegeven ?? "")}
              empty=""
            />
          </div>
        )}
      </section>
    </div>
  );
}
